import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import api from '../../services/api';
import styles from './ClientDetailPage.module.css';
import InitialsAvatar from '../../components/common/InitialsAvatar';
import TicketDetailModal from '../../components/admin/TicketDetailModal';
import OpenTicketModal from '../../components/admin/OpenTicketModal';
import ListLoadError from '../../components/admin/ListLoadError';
import { getSupplyLabel } from '../../utils/supplyTerms';
import { calculateOrderCostCents } from './orderCostMath';

// Urgency levels configuration
const urgencyLevels = [
  { value: 'low', label: 'Low', color: '#6b7280', bgColor: '#f3f4f6' },
  { value: 'medium', label: 'Medium', color: '#d97706', bgColor: '#fef3c7' },
  { value: 'high', label: 'High', color: '#ea580c', bgColor: '#ffedd5' },
  { value: 'critical', label: 'Critical', color: '#dc2626', bgColor: '#fee2e2' }
];

const getUrgencyConfig = (urgency) => urgencyLevels.find(u => u.value === urgency) || urgencyLevels[0];

// Note type configuration
const noteTypeConfig = {
  manual: { label: 'Note', color: '#6b7280', bgColor: '#f9fafb', borderColor: '#d1d5db' },
  system: { label: 'System', color: '#2563eb', bgColor: '#eff6ff', borderColor: '#93c5fd' },
  action_log: { label: 'Action', color: '#ea580c', bgColor: '#fff7ed', borderColor: '#fdba74' },
  interaction: { label: 'Interaction', color: '#7c3aed', bgColor: '#f5f3ff', borderColor: '#c4b5fd' }
};

// Accounts Receivable status display (mirrors the A/R tab's badge palette)
const arStatusMeta = {
  active:         { label: 'Active',         bg: '#e8f5e9', color: '#2e7d32' },
  pending_charge: { label: 'Pending Charge', bg: '#fef3cd', color: '#92610e' },
  settled:        { label: 'Settled',        bg: '#e0f2fe', color: '#0369a1' },
  voided:         { label: 'Voided',         bg: '#fce4e4', color: '#b91c1c' },
  locked:         { label: 'Locked',         bg: '#f3f0eb', color: '#78716c' }
};

const arOpenStatuses = ['active', 'pending_charge', 'locked'];

const getArStatusMeta = (status) => arStatusMeta[status] || { label: status || 'Unknown', bg: '#f3f0eb', color: '#78716c' };

const formatCents = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Expanded interaction types
const interactionTypes = [
  { value: 'phone_inbound', label: 'Phone Call (inbound)', icon: '📞' },
  { value: 'phone_outbound', label: 'Phone Call (outbound)', icon: '📱' },
  { value: 'email_sent', label: 'Email Sent', icon: '📧' },
  { value: 'email_received', label: 'Email Received', icon: '📨' },
  { value: 'sms_sent', label: 'SMS Sent', icon: '💬' },
  { value: 'internal_note', label: 'Internal Note', icon: '📝' },
  { value: 'account_review', label: 'Account Review', icon: '🔍' },
  { value: 'billing_question', label: 'Billing Question', icon: '💳' },
  { value: 'support_followup', label: 'Support Follow-Up', icon: '🔄' },
  { value: 'agreement_question', label: 'Agreement Question', icon: '📋' },
  { value: 'password_help', label: 'Password Help', icon: '🔑' },
  { value: 'escalation', label: 'Escalation', icon: '🚨' },
  { value: 'other', label: 'Other', icon: '📌' }
];

const getInteractionLabel = (value) => {
  const found = interactionTypes.find(t => t.value === value);
  return found ? `${found.icon} ${found.label}` : value;
};

// Format relative time
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format absolute date+time in EST
const formatAbsoluteTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  }) + ' EST';
};

// Get date group for a note
const getDateGroup = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today); weekAgo.setDate(weekAgo.getDate() - 7);
  if (date >= today) return 'Today';
  if (date >= yesterday) return 'Yesterday';
  if (date >= weekAgo) return 'This Week';
  return 'Older';
};

// Linkify URLs in text (safe - no HTML execution)
const linkifyText = (text) => {
  if (!text) return '';
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (<a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{part}</a>);
    }
    return part;
  });
};

// Group notes by date
const groupNotesByDate = (notes) => {
  const groups = {};
  notes.forEach(note => {
    const group = getDateGroup(note.createdAt);
    if (!groups[group]) groups[group] = [];
    groups[group].push(note);
  });
  return groups;
};

// Check if subscription tier supports dependents
const tierSupportsDependents = (plan) => {
  if (!plan) return false;
  return plan.includes('spouse') || plan.includes('family');
};

// Format date of birth for display
// DOB is a pg `date` (date-only): '1975-06-14' parses as UTC midnight, so reading it back
// with a local formatter shows the PREVIOUS day for every negative-offset operator.
const formatDOB = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// Relationship display config
const relationshipLabels = { spouse: 'Spouse', child: 'Child', other: 'Other' };

// Ticket status badge config
const ticketStatusConfig = {
  open: { label: 'Open', color: '#2563eb', bgColor: '#eff6ff' },
  in_progress: { label: 'In Progress', color: '#2563eb', bgColor: '#eff6ff' },
  waiting: { label: 'Waiting', color: '#d97706', bgColor: '#fef3c7' },
  resolved: { label: 'Resolved', color: '#16a34a', bgColor: '#f0fdf4' },
  closed: { label: 'Closed', color: '#6b7280', bgColor: '#f3f4f6' }
};

// Ticket priority badge config
const ticketPriorityConfig = {
  low: { label: 'Low', color: '#6b7280', bgColor: '#f3f4f6' },
  medium: { label: 'Medium', color: '#2563eb', bgColor: '#eff6ff' },
  high: { label: 'High', color: '#ea580c', bgColor: '#fff7ed' },
  urgent: { label: 'Urgent', color: '#dc2626', bgColor: '#fee2e2' }
};

// Source/contact method display labels
const sourceLabels = {
  'member_portal': 'Member Portal',
  'email': 'Email',
  'phone_call': 'Phone Call',
  'internal_follow_up': 'Internal Follow-Up',
  'support_inbox': 'Support Inbox',
  'other': 'Other'
};


const ClientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromOrders = location.state?.from === "orders";
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [client, setClient] = useState(null);
  const [orderSummary, setOrderSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [welcomeEmailModalOpen, setWelcomeEmailModalOpen] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(false);
  const [welcomeCooldown, setWelcomeCooldown] = useState(0);
  const [unlocking, setUnlocking] = useState(false);
  const [forceLogoutModalOpen, setForceLogoutModalOpen] = useState(false);
  const [forcingLogout, setForcingLogout] = useState(false);
  const [togglingVerification, setTogglingVerification] = useState(false);
  const [editContactModalOpen, setEditContactModalOpen] = useState(false);
  const [editContactData, setEditContactData] = useState({ email: '', phone: '', street: '', city: '', state: '', zipCode: '', agencyId: '' });
  const [savingContact, setSavingContact] = useState(false);
  const [contactErrors, setContactErrors] = useState({});
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [updatingUrgency, setUpdatingUrgency] = useState(false);
  const [updatingCalled, setUpdatingCalled] = useState(false);
  const [agencies, setAgencies] = useState([]);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  // Interaction logging state
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ type: '', notes: '', followUpDate: '', relatedTicketId: '' });
  const [savingInteraction, setSavingInteraction] = useState(false);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesPagination, setNotesPagination] = useState(null);
  const [notesError, setNotesError] = useState(null);
  const [notesSortOrder, setNotesSortOrder] = useState('desc');
  const [noteFilter, setNoteFilter] = useState('all');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteExpanded, setNewNoteExpanded] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Agreements state
  const [agreements, setAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [agreementsError, setAgreementsError] = useState(null);

  // Accounts Receivable state (read-only view of the Task 16 member filter)
  const [arRecords, setArRecords] = useState([]);

  // Activity timeline state
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);
  const [activityPagination, setActivityPagination] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const [activityExpanded, setActivityExpanded] = useState(true);
  const [expandedAgreement, setExpandedAgreement] = useState(null);

  // Support tickets state
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState({});
  const [ticketMessagesError, setTicketMessagesError] = useState({});
  const [closingTicket, setClosingTicket] = useState(null);
  const [claimingTicket, setClaimingTicket] = useState(null);
  const [viewTicketId, setViewTicketId] = useState(null);
  const [viewTicketModalOpen, setViewTicketModalOpen] = useState(false);
  const [openTicketModalOpen, setOpenTicketModalOpen] = useState(false);

  // Dependents state
  const [dependents, setDependents] = useState([]);
  const [dependentsLoading, setDependentsLoading] = useState(false);
  const [dependentsError, setDependentsError] = useState(null);
  const [dependentModalOpen, setDependentModalOpen] = useState(false);
  const [editingDependent, setEditingDependent] = useState(null);
  const [dependentFormData, setDependentFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '', relationship: 'spouse'
  });
  const [savingDependent, setSavingDependent] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingDependent, setDeletingDependent] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    loadClient();
    loadNotes();
    loadAgencies();
    if (hasPermission('orders.view')) loadOrders();
  }, [id]);

  // Load dependents when client data is available
  useEffect(() => {
    if (client) {
      loadDependents();
      loadAgreements();
      loadActivityLog();
      loadSupportTickets();
      if (hasPermission('accounts_receivable.view')) loadAccountsReceivable();
    }
  }, [client?.subscriptionPlan]);

  // Cooldown timer for welcome email
  useEffect(() => {
    if (welcomeCooldown > 0) {
      const timer = setTimeout(() => setWelcomeCooldown(welcomeCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [welcomeCooldown]);

  const loadClient = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/members/${id}`);
      setClient(response.member);
      setOrderSummary(response.orderSummary);
    } catch (error) {
      toast.error('Failed to load client details');
      navigate('/admin/members');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const response = await api.get(`/admin/orders?userId=${id}&limit=50`);
      setOrders(response.orders || []);
    } catch (error) {
      setOrdersError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const getOrderStatusStyle = (status) => {
    const map = {
      pending:      { bg: '#fef3cd', color: '#92610e' },
      under_review: { bg: '#fff0e0', color: '#c2600a' },
      approved:     { bg: '#e8f5e9', color: '#2e7d32' },
      denied:       { bg: '#fce4e4', color: '#b91c1c' },
      processing:   { bg: '#fff4e6', color: '#b45309' },
      shipped:      { bg: '#e0f2fe', color: '#0369a1' },
      delivered:    { bg: '#dcfce7', color: '#15803d' },
      cancelled:    { bg: '#f3f0eb', color: '#78716c' },
      payment_failed: { bg: '#fce4e4', color: '#b91c1c' },
      payment_held: { bg: '#fef3cd', color: '#92610e' }
    };
    return map[status] || map.cancelled;
  };

  // The gate MUST use the shared four-column helper: a three-column sum hid the profit of any
  // order whose only cost is shipping, while the value below comes from the backend's four. (card 28342)
  const formatOrderProfit = (order) => {
    const costs = calculateOrderCostCents(order);
    if (costs === 0) return { display: '\u2014', color: 'inherit' };
    const profit = order.profit ?? 0;
    const dollars = (profit / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return { display: `$${dollars}`, color: profit >= 0 ? '#15803d' : '#b91c1c' };
  };

  // Filter notes by type
  const filteredNotes = noteFilter === 'all'
    ? notes
    : notes.filter(n => {
        if (noteFilter === 'notes') return n.noteType === 'manual';
        if (noteFilter === 'actions') return n.noteType === 'action_log';
        if (noteFilter === 'interactions') return n.noteType === 'interaction';
        return true;
      });

  const loadNotes = async (page = 1) => {
    setNotesLoading(true);
    setNotesError(null);
    try {
      const response = await api.get(`/admin/members/${id}/notes`, {
        params: { page, limit: 20, sortOrder: notesSortOrder }
      });
      setNotes(response.notes || []);
      setNotesPagination(response.pagination);
    } catch (error) {
      setNotesError('Could not load notes');
      console.error('Failed to load notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  const loadAgencies = async () => {
    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, and the old catch reported
    // that denial as "no agencies / API unavailable", which is false on both counts.
    if (!canViewAgencies(hasPermission)) {
      setAgencies([]);
      return;
    }
    try {
      const response = await api.get('/admin/agencies');
      setAgencies(response.agencies || []);
    } catch (error) {
      console.error('Failed to load agencies:', error);
    }
  };

  const loadDependents = async () => {
    setDependentsLoading(true);
    setDependentsError(null);
    try {
      const response = await api.get(`/admin/members/${id}/dependents`);
      setDependents(response.dependents || []);
    } catch (error) {
      setDependentsError('Could not load dependents');
      console.error('Failed to load dependents:', error);
    } finally {
      setDependentsLoading(false);
    }
  };

  const loadSupportTickets = async () => {
    setTicketsLoading(true);
    setTicketsError(null);
    try {
      const response = await api.get(`/admin/support-tickets?userId=${id}`);
      setSupportTickets(response.tickets || []);
    } catch (error) {
      setTicketsError('Could not load support tickets');
      console.error('Failed to load support tickets:', error);
    }
    setTicketsLoading(false);
  };

  // Kept separate from the expand/collapse toggle below so the error state's Retry can re-issue
  // the request directly. Routing Retry back through loadTicketMessages would hit its
  // `expandedTicket === ticketId` guard and COLLAPSE the thread instead of refetching it.
  const fetchTicketMessages = async (ticketId) => {
    setTicketMessagesError(prev => ({ ...prev, [ticketId]: null }));
    try {
      const response = await api.get(`/admin/support-tickets/${ticketId}`);
      setTicketMessages(prev => ({ ...prev, [ticketId]: response.ticket?.messages || [] }));
    } catch (error) {
      // Without this the thread stays `undefined`, which the render cannot tell apart from
      // "still loading" — the user just watches a spinner forever. Mirrors the error handling
      // loadSupportTickets already does for the list this thread hangs off.
      setTicketMessagesError(prev => ({ ...prev, [ticketId]: 'Could not load ticket messages' }));
      console.error('Failed to load ticket messages:', error);
    }
  };

  const loadTicketMessages = async (ticketId) => {
    if (expandedTicket === ticketId) { setExpandedTicket(null); return; }
    setExpandedTicket(ticketId);
    if (ticketMessages[ticketId]) return;
    await fetchTicketMessages(ticketId);
  };

  const handleCloseTicket = async (ticketId) => {
    setClosingTicket(ticketId);
    try {
      await api.patch(`/admin/support-tickets/${ticketId}/status`, { status: 'closed' });
      toast.success('Ticket closed');
      setSupportTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: 'closed' } : t));
      loadNotes(1);
    } catch (error) {
      toast.error('Failed to close ticket');
    }
    setClosingTicket(null);
  };

  const handleViewTicketDetail = (ticketId) => {
    setViewTicketId(ticketId);
    setViewTicketModalOpen(true);
  };

  // Claim from the profile — identical behavior to the Support Tickets page
  // (POST /claim; first-claim-wins 403 names the current owner).
  const handleClaimProfileTicket = async (ticket) => {
    setClaimingTicket(ticket.id);
    try {
      await api.post(`/admin/support-tickets/${ticket.id}/claim`);
      toast.success('Ticket claimed');
      loadSupportTickets();
    } catch (error) {
      if (error.status === 403) {
        let owner = ticket.assignedTo?.fullName;
        try {
          const detail = await api.get(`/admin/support-tickets/${ticket.id}`);
          owner = detail.ticket?.assignedTo?.fullName || owner;
        } catch (e) {
          // keep the list-derived owner name
        }
        toast.error(
          owner
            ? `Already claimed by ${owner}`
            : 'This ticket is already claimed by another agent'
        );
        loadSupportTickets();
      } else {
        toast.error('Failed to claim ticket');
      }
    }
    setClaimingTicket(null);
  };


  const loadAgreements = async () => {
    setAgreementsLoading(true);
    setAgreementsError(null);
    try {
      const response = await api.get(`/admin/members/${id}/agreements`);
      setAgreements(response.agreements || []);
    } catch (error) {
      console.error('Failed to load agreements:', error);
      setAgreementsError('Could not load agreements');
    }
    setAgreementsLoading(false);
  };

  const loadAccountsReceivable = async () => {
    try {
      const response = await api.get(`/admin/accounts-receivable?member=${id}`);
      setArRecords(response.records || []);
    } catch (error) {
      // Section stays hidden on failure — no toast
      console.error('Failed to load accounts receivable:', error);
    }
  };

  // Activity Log loader (was missing in Phase 1!)
  const loadActivityLog = async (page = 1, filterOverride) => {
    setActivityLoading(true);
    setActivityError(null);
    try {
      const f = filterOverride !== undefined ? filterOverride : activityFilter;
      const params = { page, limit: 50 };
      if (f && f !== 'all') params.types = f;
      const response = await api.get(`/admin/members/${id}/activity-log`, { params });
      setActivityLog(response.data || []);
      setActivityPagination(response.pagination || null);
    } catch (error) {
      console.error('Failed to load activity log:', error);
      setActivityError('Could not load activity log');
    }
    setActivityLoading(false);
  };

  const handleAddDependent = () => {
    setEditingDependent(null);
    setDependentFormData({ firstName: '', lastName: '', email: '', phone: '', dateOfBirth: '', relationship: 'spouse' });
    setDependentModalOpen(true);
  };

  const handleEditDependent = (dependent) => {
    setEditingDependent(dependent);
    setDependentFormData({
      firstName: dependent.firstName || '', lastName: dependent.lastName || '',
      email: dependent.email || '', phone: dependent.phone || '',
      dateOfBirth: dependent.dateOfBirth ? dependent.dateOfBirth.split('T')[0] : '',
      relationship: dependent.relationship || 'spouse'
    });
    setDependentModalOpen(true);
  };

  const handleSaveDependent = async () => {
    if (!dependentFormData.firstName?.trim() || !dependentFormData.lastName?.trim()) { toast.error('First name and last name are required'); return; }
    if (!dependentFormData.relationship) { toast.error('Relationship is required'); return; }
    if (!editingDependent && !dependentFormData.email?.trim()) { toast.error('Email is required for new dependents'); return; }
    setSavingDependent(true);
    try {
      const payload = {
        firstName: dependentFormData.firstName.trim(), lastName: dependentFormData.lastName.trim(),
        email: dependentFormData.email?.trim() || null, phone: dependentFormData.phone?.trim() || null,
        dateOfBirth: dependentFormData.dateOfBirth || null, relationship: dependentFormData.relationship
      };
      if (editingDependent) {
        await api.put(`/admin/members/${id}/dependents/${editingDependent.id}`, payload);
        toast.success('Dependent updated');
      } else {
        await api.post(`/admin/members/${id}/dependents`, payload);
        toast.success('Dependent added');
      }
      setDependentModalOpen(false);
      loadDependents();
    } catch (error) {
      toast.error(error.message || 'Failed to save dependent');
    } finally { setSavingDependent(false); }
  };

  const handleDeleteClick = (dependent) => { setDeletingDependent(dependent); setDeleteModalOpen(true); };

  const handleConfirmDelete = async () => {
    if (!deletingDependent) return;
    setConfirmingDelete(true);
    try {
      await api.delete(`/admin/members/${id}/dependents/${deletingDependent.id}`);
      toast.success('Dependent removed');
      setDeleteModalOpen(false);
      setDeletingDependent(null);
      loadDependents();
    } catch (error) {
      toast.error(error.message || 'Failed to delete dependent');
    } finally { setConfirmingDelete(false); }
  };

  const handleCreateNote = async () => {
    if (!newNoteContent.trim() || savingNote) return;
    setSavingNote(true);
    const optimisticNote = {
      id: `temp-${Date.now()}`, content: newNoteContent.trim(), noteType: 'manual',
      authorName: 'You', createdAt: new Date().toISOString(), isPinned: false, _optimistic: true
    };
    if (notesSortOrder === 'desc') { setNotes(prev => [optimisticNote, ...prev]); }
    else { setNotes(prev => [...prev, optimisticNote]); }
    setNewNoteContent('');
    setNewNoteExpanded(false);
    try {
      const response = await api.post(`/admin/members/${id}/notes`, { content: newNoteContent.trim(), noteType: 'manual' });
      setNotes(prev => prev.map(note => note.id === optimisticNote.id ? response.note : note));
      toast.success('Note added');
    } catch (error) {
      setNotes(prev => prev.filter(note => note.id !== optimisticNote.id));
      toast.error(error.message || 'Failed to add note');
      setNewNoteContent(optimisticNote.content);
      setNewNoteExpanded(true);
    } finally { setSavingNote(false); }
  };

  const handleCancelNote = () => { setNewNoteContent(''); setNewNoteExpanded(false); };

  // A 2xx is NOT proof the mail went out. Both endpoints answer with the delivery
  // contract ({ delivered, suppressed }); a SUPPRESSED send gets its own warning
  // toast with its own copy, never a success toast, because the whole point of this
  // fix is that an operator can tell a swallowed email from a sent one at a glance.
  const toastEmailOutcome = (data, sentMsg, failMsg) => {
    if (data?.suppressed) return toast.warning('Email SUPPRESSED (sandbox) — not delivered');
    if (data?.delivered === false) return toast.error(data.message || failMsg);
    return toast.success(sentMsg);
  };

  const handleResetPassword = async () => {
    setSendingReset(true);
    try {
      const data = await api.post(`/admin/members/${id}/reset-password`);
      toastEmailOutcome(data, 'Password reset email sent', 'Failed to send password reset email');
      setResetPasswordModalOpen(false);
    }
    catch (error) { toast.error(error.message || 'Failed to send password reset email'); }
    finally { setSendingReset(false); }
  };

  const handleResendWelcome = async () => {
    setSendingWelcome(true);
    try {
      const data = await api.post(`/admin/members/${id}/resend-welcome`);
      toastEmailOutcome(data, 'Welcome email sent', 'Failed to send welcome email');
      setWelcomeEmailModalOpen(false); setWelcomeCooldown(30);
    }
    catch (error) { toast.error(error.message || 'Failed to send welcome email'); }
    finally { setSendingWelcome(false); }
  };

  const handleUnlockAccount = async () => {
    setUnlocking(true);
    try { await api.post(`/admin/members/${id}/unlock`); toast.success('Account unlocked successfully'); await loadClient(); }
    catch (error) { toast.error(error.message || 'Failed to unlock account'); }
    finally { setUnlocking(false); }
  };

  const handleForceLogout = async () => {
    setForcingLogout(true);
    try { await api.post(`/admin/members/${id}/force-logout`); toast.success('All sessions terminated'); setForceLogoutModalOpen(false); }
    catch (error) { toast.error(error.message || 'Failed to terminate sessions'); }
    finally { setForcingLogout(false); }
  };

  const handleToggleVerification = async () => {
    setTogglingVerification(true);
    const newVerifiedState = !client.isVerified;
    try {
      await api.patch(`/admin/members/${id}`, { isVerified: newVerifiedState });
      toast.success(newVerifiedState ? 'Email marked as verified' : 'Email marked as unverified');
      await loadClient();
    } catch (error) { toast.error(error.message || 'Failed to update verification status'); }
    finally { setTogglingVerification(false); }
  };

  const openEditContactModal = () => {
    setEditContactData({
      email: client.email || '', phone: client.profile?.phone || '',
      street: client.profile?.address?.street || '', city: client.profile?.address?.city || '',
      state: client.profile?.address?.state || '', zipCode: client.profile?.address?.zipCode || '',
      agencyId: client.agency?.id || ''
    });
    setContactErrors({});
    setEditContactModalOpen(true);
  };

  const validateContactForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    if (!editContactData.email.trim()) { errors.email = 'Email is required'; }
    else if (!emailRegex.test(editContactData.email)) { errors.email = 'Invalid email format'; }
    if (editContactData.phone && !phoneRegex.test(editContactData.phone.replace(/\D/g, ''))) { errors.phone = 'Phone must be 10 digits'; }
    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveContact = async () => {
    if (!validateContactForm()) return;
    setSavingContact(true);
    const emailChanged = editContactData.email !== client.email;
    const phoneChanged = editContactData.phone !== (client.profile?.phone || '');
    const addressChanged = editContactData.street !== (client.profile?.address?.street || '') ||
      editContactData.city !== (client.profile?.address?.city || '') ||
      editContactData.state !== (client.profile?.address?.state || '') ||
      editContactData.zipCode !== (client.profile?.address?.zipCode || '');
    try {
      const updatePayload = {};
      const agencyChanged = editContactData.agencyId !== (client.agency?.id || '');
      if (agencyChanged) updatePayload.agencyId = editContactData.agencyId || null;
      if (emailChanged) { updatePayload.email = editContactData.email; updatePayload.isVerified = false; }
      if (phoneChanged || addressChanged) {
        updatePayload.profile = updatePayload.profile || {};
        if (phoneChanged) updatePayload.profile.phone = editContactData.phone || null;
        if (addressChanged) updatePayload.profile.address = { street: editContactData.street || '', city: editContactData.city || '', state: editContactData.state || '', zipCode: editContactData.zipCode || '' };
      }
      if (Object.keys(updatePayload).length > 0) {
        await api.patch(`/admin/members/${id}`, updatePayload);
        toast.success(emailChanged ? 'Contact info updated. Email verification has been reset.' : 'Contact info updated successfully');
        await loadClient();
      }
      setEditContactModalOpen(false);
    } catch (error) { toast.error(error.message || 'Failed to update contact info'); }
    finally { setSavingContact(false); }
  };

  const handleToggleActive = async () => {
    setTogglingActive(true);
    const newActiveState = !client.isActive;
    try {
      await api.patch(`/admin/members/${id}`, { isActive: newActiveState });
      if (!newActiveState) { await api.post(`/admin/members/${id}/force-logout`); toast.success('Account deactivated and all sessions terminated'); }
      else { toast.success('Account reactivated successfully'); }
      setDeactivateModalOpen(false);
      await loadClient();
    } catch (error) { toast.error(error.message || `Failed to ${newActiveState ? 'reactivate' : 'deactivate'} account`); }
    finally { setTogglingActive(false); }
  };

  const handleUrgencyChange = async (e) => {
    const newUrgency = e.target.value;
    if (newUrgency === client.urgency) return;
    setUpdatingUrgency(true);
    try { await api.patch(`/admin/members/${id}`, { urgency: newUrgency }); toast.success(`Urgency updated to ${getUrgencyConfig(newUrgency).label}`); await loadClient(); }
    catch (error) { toast.error(error.message || 'Failed to update urgency'); }
    finally { setUpdatingUrgency(false); }
  };

  const handleToggleCalled = async () => {
    setUpdatingCalled(true);
    try {
      const newStatus = client.calledStatus === 'called' ? 'not_called' : 'called';
      await api.patch(`/admin/members/${id}/called-status`, { status: newStatus });
      setClient(prev => ({ ...prev, calledStatus: newStatus, calledAt: newStatus === 'called' ? new Date().toISOString() : null }));
      toast.success(newStatus === 'called' ? 'Marked as called' : 'Marked as not called');
      loadNotes();
    } catch (error) { toast.error('Failed to update called status'); }
    finally { setUpdatingCalled(false); }
  };

  // Enhanced interaction logging
  const handleLogInteraction = async () => {
    if (!interactionForm.type || !interactionForm.notes.trim()) return;
    setSavingInteraction(true);
    try {
      await api.post(`/admin/members/${id}/interactions`, {
        interactionType: interactionForm.type,
        notes: interactionForm.notes.trim(),
        followUpDate: interactionForm.followUpDate || null,
        relatedTicketId: interactionForm.relatedTicketId || null
      });
      toast.success('Interaction logged');
      setInteractionModalOpen(false);
      setInteractionForm({ type: '', notes: '', followUpDate: '', relatedTicketId: '' });
      loadNotes(1);
      loadActivityLog(1);
    } catch (error) {
      toast.error(error.message || 'Failed to log interaction');
    } finally { setSavingInteraction(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (<div className={styles.page}><div className={styles.loading}><LoadingSpinner /></div></div>);
  if (!client) return (<div className={styles.page}><div className={styles.empty}>Client not found</div></div>);

  const fullName = client.profile?.fullName || [client.profile?.firstName, client.profile?.lastName].filter(Boolean).join(' ') || 'No name';

  // Compute ticket counts
  const openTicketCount = supportTickets.filter(t => t.status === 'open' || t.status === 'in_progress' || t.status === 'waiting').length;
  const closedTicketCount = supportTickets.filter(t => t.status === 'closed' || t.status === 'resolved').length;

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(cameFromOrders ? '/admin/orders' : '/admin/members')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          {cameFromOrders ? 'Back to Orders' : 'Back to Members'}
        </button>
        <div className={styles.headerContent}>
          <div className={styles.clientHeader}>
            <div className={styles.avatar}>{client.profile?.firstName?.[0] || client.email[0].toUpperCase()}</div>
            <div><h1>{fullName}</h1><p className={styles.email}>{client.email}</p></div>
          </div>
          <div className={styles.headerActions}>
            {client.isLocked && (<Button onClick={handleUnlockAccount} disabled={unlocking}>{unlocking ? 'Unlocking...' : 'Unlock Account'}</Button>)}
            <Button variant="outline" onClick={() => setForceLogoutModalOpen(true)}>Force Logout</Button>
            <Button variant="outline" onClick={() => setWelcomeEmailModalOpen(true)} disabled={welcomeCooldown > 0}>{welcomeCooldown > 0 ? `Wait ${welcomeCooldown}s` : 'Resend Welcome'}</Button>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(true)}>Reset Password</Button>
            {/* Prime Directive 3 (UI layer): never offer Deactivate for a Super Admin.
                Reactivate stays available if a Super Admin is somehow inactive. */}
            {!(client.isSuperAdmin && client.isActive) && (
              <Button variant={client.isActive ? 'danger' : 'primary'} onClick={() => setDeactivateModalOpen(true)}>{client.isActive ? 'Deactivate Account' : 'Reactivate Account'}</Button>
            )}
          </div>
        </div>
      </div>

      {/* Status Badges */}
      <div className={styles.statusRow}>
        <div className={styles.activeGroup}>
          {/* Card 30787. A pending member has is_active=false, so without this branch this badge
              reads "Inactive" — i.e. "membership ended" — directly contradicting the Activation
              State field below. Purely additive: activationState is null for every member with no
              schedule row, so the Active/Inactive arms are unchanged for them. */}
          <span className={`${styles.statusBadge} ${client.activationState === 'pending' ? styles.pending : client.isActive ? styles.active : styles.inactive}`}>{client.activationState === 'pending' ? 'Pending' : client.isActive ? 'Active' : 'Inactive'}</span>
        </div>
        <div className={styles.verificationGroup}>
          <span className={`${styles.statusBadge} ${client.isVerified ? styles.verified : styles.unverified}`}>{client.isVerified ? 'Email Verified' : 'Email Not Verified'}</span>
          <button className={styles.toggleBtn} onClick={handleToggleVerification} disabled={togglingVerification} title={client.isVerified ? 'Mark as unverified' : 'Mark as verified'}>{togglingVerification ? '...' : client.isVerified ? 'Unverify' : 'Verify'}</button>
        </div>
        {client.isLocked && (<span className={`${styles.statusBadge} ${styles.locked}`}>Account Locked</span>)}
        <span className={`${styles.roleBadge} ${styles[client.role] || ''}`}>{client.role}</span>
        <div className={styles.urgencySelector}>
          <label className={styles.urgencyLabel}>Priority:</label>
          <select className={styles.urgencyDropdown} value={client.urgency || 'low'} onChange={handleUrgencyChange} disabled={updatingUrgency}
            style={{ backgroundColor: getUrgencyConfig(client.urgency || 'low').bgColor, color: getUrgencyConfig(client.urgency || 'low').color, borderColor: getUrgencyConfig(client.urgency || 'low').color }}>
            {urgencyLevels.map(level => (<option key={level.value} value={level.value}>{level.label}</option>))}
          </select>
        </div>
        <div className={styles.calledGroup}>
          <span className={styles.calledDot} style={{ backgroundColor: client.calledStatus === 'called' ? '#059669' : '#f97316' }} />
          <span className={styles.calledLabel}>{client.calledStatus === 'called' ? 'Called' : 'Not Called'}</span>
          {client.calledStatus === 'called' && client.calledAt && (
            <span className={styles.calledTimestamp}>{new Date(client.calledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
          )}
          <button className={styles.toggleBtn} onClick={handleToggleCalled} disabled={updatingCalled} title={client.calledStatus === 'called' ? 'Mark as not called' : 'Mark as called'}>
            {updatingCalled ? '...' : client.calledStatus === 'called' ? 'Uncall' : 'Mark Called'}
          </button>
        </div>
        <button className={styles.openTicketBtn} onClick={() => setInteractionModalOpen(true)} style={{ marginLeft: 'auto' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
          Log Interaction
        </button>
      </div>

      {/* Main Content Grid */}
      <div className={styles.grid}>
        <Card className={styles.card}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>Basic Information</h2>
            <button className={styles.editBtn} onClick={openEditContactModal} title="Edit contact info">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
              Edit
            </button>
          </div>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}><span className={styles.label}>Member ID</span><span className={styles.value} style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{client.memberId || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Membership Tier</span><span className={styles.value}>{client.membershipTierName || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Email</span><span className={styles.value}>{client.email}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Phone</span><span className={styles.value}>{client.profile?.phone || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Date of Birth</span><span className={styles.value}>{client.profile?.dateOfBirth || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Gender</span><span className={styles.value}>{client.profile?.gender || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Agency</span><span className={styles.value}>{client.agency ? (<Link to={`/admin/agencies/${client.agency.id}`} className={styles.agencyLink}>{client.agency.name}</Link>) : '—'}</span></div>
          </div>
        </Card>
        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Address</h2>
          {client.profile?.address ? (
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}><span className={styles.label}>Street</span><span className={styles.value}>{client.profile.address.street || '—'}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>City</span><span className={styles.value}>{client.profile.address.city || '—'}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>State</span><span className={styles.value}>{client.profile.address.state || '—'}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>ZIP Code</span><span className={styles.value}>{client.profile.address.zipCode || '—'}</span></div>
            </div>
          ) : (<p className={styles.emptyText}>No address on file</p>)}
        </Card>
        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Account Details</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}><span className={styles.label}>Telemed ID</span><span className={`${styles.value} ${client.profile?.drCareMemberId ? styles.telemedId : ''}`}>{client.profile?.drCareMemberId || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Member Since</span><span className={styles.value}>{formatDate(client.createdAt)}</span></div>
            {/* Card 30787. Rendered RAW, and deliberately NOT through formatDate: that helper is
                `new Date(s).toLocaleDateString(...)`, and a bare YYYY-MM-DD parses as UTC midnight
                and prints one day EARLIER in any negative-offset zone. Migration 071 stores this as
                `date` for exactly that reason, and `readEffectiveDate` refuses formatISODate for it
                too — the column, the API and this cell must all agree on the same nine characters. */}
            <div className={styles.infoItem}><span className={styles.label}>Effective Date</span><span className={styles.value}>{client.effectiveDate || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Activation State</span><span className={styles.value}>{client.activationState || '—'}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Last Login</span><span className={styles.value}>{formatDate(client.lastLogin)}</span></div>
            <div className={styles.infoItem}><span className={styles.label}>Login Attempts</span><span className={styles.value}>{client.loginAttempts || 0}</span></div>
            {client.lockUntil && (<div className={styles.infoItem}><span className={styles.label}>Locked Until</span><span className={styles.value}>{formatDate(client.lockUntil)}</span></div>)}
          </div>
        </Card>
        <Card className={styles.card}>
          <h2 className={styles.cardTitle}>Order Summary</h2>
          {orderSummary ? (
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}><span className={styles.label}>Total Orders</span><span className={styles.value}>{orderSummary.totalOrders || 0}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>Total Spent</span><span className={styles.value}>${((orderSummary.totalSpent || 0) / 100).toFixed(2)}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>Last Order</span><span className={styles.value}>{formatDate(orderSummary.lastOrderDate)}</span></div>
            </div>
          ) : (<p className={styles.emptyText}>No orders yet</p>)}
        </Card>
      </div>

      {/* Dependents Section */}
      {tierSupportsDependents(client.subscriptionPlan) && (
        <div className={styles.dependentsSection} id="dependents">
          <div className={styles.dependentsSectionHeader}>
            <h2 className={styles.sectionTitle}>Dependents</h2>
            <Button size="small" onClick={handleAddDependent}>Add Dependent</Button>
          </div>
          {dependentsLoading ? (<div className={styles.dependentsLoading}><LoadingSpinner size="small" /></div>
          ) : dependentsError && dependents.length === 0 ? (
            <ListLoadError message={dependentsError} onRetry={() => loadDependents()} />
          ) : dependents.length === 0 ? (<div className={styles.dependentsEmpty}><p>No dependents added</p><span>Add family members covered under this membership</span></div>
          ) : (
            <>
              <div className={styles.dependentsTable}>
                <table><thead><tr><th>Name</th><th>Status</th><th>Relationship</th><th>Date of Birth</th><th>Email</th><th>Phone</th><th>Date Added</th><th>Actions</th></tr></thead>
                  <tbody>{dependents.map(dep => (
                    <tr key={dep.id}>
                      <td>{dep.firstName} {dep.lastName}</td>
                      <td><span className={styles.statusBadge + (dep.accountStatus === "active" ? " " + styles.statusActive : " " + styles.statusPending)}>{dep.accountStatus === "active" ? "Active" : "Pending"}</span></td>
                      <td>{relationshipLabels[dep.relationship] || dep.relationship}</td>
                      <td>{formatDOB(dep.dateOfBirth)}</td>
                      <td>{dep.email || '—'}</td><td>{dep.phone || '—'}</td>
                      <td>{dep.dateAdded ? new Date(dep.dateAdded).toLocaleDateString() : '—'}</td>
                      <td><div className={styles.actionBtns}><button className={styles.editBtn} onClick={() => handleEditDependent(dep)} title="Edit dependent">Edit</button><button className={styles.deleteBtn} onClick={() => handleDeleteClick(dep)} title="Remove dependent">Delete</button></div></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div className={styles.dependentsCards}>
                {dependents.map(dep => (
                  <div key={dep.id} className={styles.dependentCard}>
                    <div className={styles.dependentCardHeader}><span className={styles.dependentName}>{dep.firstName} {dep.lastName}</span><span className={styles.relationshipBadge}>{relationshipLabels[dep.relationship] || dep.relationship}</span></div>
                    <div className={styles.dependentCardDetails}>
                      <div className={styles.detailRow}><span className={styles.detailLabel}>DOB:</span><span>{formatDOB(dep.dateOfBirth)}</span></div>
                      {dep.email && (<div className={styles.detailRow}><span className={styles.detailLabel}>Email:</span><span>{dep.email}</span></div>)}
                      {dep.phone && (<div className={styles.detailRow}><span className={styles.detailLabel}>Phone:</span><span>{dep.phone}</span></div>)}
                    </div>
                    <div className={styles.dependentCardActions}><button onClick={() => handleEditDependent(dep)}>Edit</button><button onClick={() => handleDeleteClick(dep)} className={styles.deleteBtn}>Delete</button></div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Orders Section */}
      {hasPermission('orders.view') && (
        <div className={styles.dependentsSection} id="orders">
          <div className={styles.dependentsSectionHeader}>
            <h2 className={styles.sectionTitle}>Orders</h2>
          </div>
          {ordersLoading ? (
            <div className={styles.dependentsLoading}><LoadingSpinner size="small" /></div>
          ) : ordersError ? (
            <div className={styles.emptyStateBox}>
              <p style={{ margin: '0.5rem 0 0', color: '#b91c1c', fontSize: '0.875rem' }}>{ordersError}</p>
              <button onClick={loadOrders} style={{ marginTop: '0.5rem', color: '#f97316', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>Retry</button>
            </div>
          ) : orders.length === 0 ? (
            <div className={styles.emptyStateBox}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
              <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>No orders found for this member.</p>
            </div>
          ) : (
            <div className={styles.dependentsTable}>
              <table>
                <thead>
                  <tr>
                    <th>Order Number</th>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Payment</th>
                    <th>Total</th>
                    <th>Profit</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const statusStyle = getOrderStatusStyle(order.status);
                    const profit = formatOrderProfit(order);
                    return (
                      <tr key={order.id}>
                        <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{order.orderNumber || '\u2014'}</td>
                        <td>
                          {(order.items || []).length > 0
                            ? order.items.map((item, i) => (
                                <div key={i} style={{ fontSize: '0.85rem' }}>
                                  {item.productName || item.product?.name || 'Unknown'}
                                  {item.pricingTierName || item.pricingTier?.name ? <span style={{ color: '#737373', fontSize: '0.75rem', marginLeft: '4px' }}>({item.pricingTierName || item.pricingTier?.name})</span> : null}
                                </div>
                              ))
                            : '\u2014'}
                        </td>
                        <td>
                          <span className={styles.statusBadge} style={{ backgroundColor: statusStyle.bg, color: statusStyle.color, border: `0.5px solid ${statusStyle.color}20` }}>
                            {order.statusLabel || order.status}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{order.payment?.status || '\u2014'}</td>
                        <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{order.pricing?.formattedTotal || '\u2014'}</td>
                        <td style={{ fontSize: '0.85rem', color: profit.color }}>{profit.display}</td>
                        <td style={{ fontSize: '0.85rem', color: '#737373' }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '\u2014'}</td>
                        <td>
                          <button className={styles.editBtn} onClick={() => navigate(`/admin/orders/${order.id}`)} title="View order details">View</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Accounts Receivable Section — hidden when the member has no A/R record */}
      {arRecords.length > 0 && (() => {
        const primary = arRecords.find(r => arOpenStatuses.includes(r.status)) || arRecords[0];
        const arStyle = getArStatusMeta(primary.status);
        const pastCount = arRecords.length - 1;
        return (
          <div className={styles.dependentsSection} id="accounts-receivable">
            <div className={styles.dependentsSectionHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2 className={styles.sectionTitle} style={{ margin: 0 }}>Accounts Receivable</h2>
                <span className={styles.statusBadge} style={{ backgroundColor: arStyle.bg, color: arStyle.color, border: `0.5px solid ${arStyle.color}20` }}>{arStyle.label}</span>
              </div>
              <Link to={`/admin/accounts-receivable?member=${id}`} className={styles.editBtn} style={{ textDecoration: 'none' }}>View Full Record</Link>
            </div>
            <div className={styles.infoGrid} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <div className={styles.infoItem}><span className={styles.label}>Outstanding Balance</span><span className={styles.value} style={{ fontSize: '1.25rem', fontWeight: 800 }}>{formatCents(primary.balanceCents)}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>Months Remaining</span><span className={styles.value}>{primary.monthsRemaining ?? '—'}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>Monthly Amount</span><span className={styles.value}>{formatCents(primary.monthlyAmountCents)}</span></div>
              <div className={styles.infoItem}><span className={styles.label}>Term Ends</span><span className={styles.value}>{primary.termEndDate ? new Date(primary.termEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span></div>
            </div>
            {primary.status === 'pending_charge' && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#92610e', background: '#fef3cd', border: '0.5px solid #92610e20', borderRadius: '8px', padding: '0.5rem 0.75rem' }}>
                Balance frozen for manual review — nothing is billed automatically.
              </div>
            )}
            {pastCount > 0 && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: 'var(--color-gray-500, #737373)' }}>
                +{pastCount} previous record{pastCount === 1 ? '' : 's'} — view all in the Accounts Receivable tab.
              </div>
            )}
          </div>
        );
      })()}

      {/* Agreements Section */}
      <div className={styles.dependentsSection} id="agreements">
        <div className={styles.dependentsSectionHeader}><h2 className={styles.sectionTitle}>Agreements</h2></div>
        {agreementsLoading ? (<div className={styles.dependentsLoading}><LoadingSpinner size="small" /></div>
        ) : agreementsError && agreements.length === 0 ? (
          <ListLoadError message={agreementsError} onRetry={() => loadAgreements()} />
        ) : agreements.length === 0 ? (
          <div className={styles.emptyStateBox}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>No agreements on file</p>
            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Subscription consent agreements will appear here</span>
          </div>
        ) : (
          <div className={styles.dependentsTable}>
            <table><thead><tr><th>Date</th><th>Product</th><th>Supply</th><th>Monthly Price</th><th>Total</th><th>Signature</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>{agreements.map(a => (
                <tr key={a.id}>
                  <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                  <td>{a.productName || '—'}</td>
                  <td>{a.supplyLabel || (a.supplyMonths != null ? getSupplyLabel(a.supplyMonths) : '—')}</td>
                  <td>{a.membershipMonthlyRate != null ? formatCents(a.membershipMonthlyRate) : (a.monthlyPrice != null ? `$${Number(a.monthlyPrice).toFixed(2)}` : '—')}</td>
                  <td>{a.membershipMonthlyRate != null && a.internalMonths ? formatCents(a.membershipMonthlyRate * a.internalMonths) : (a.totalCommitment != null ? `$${Number(a.totalCommitment).toFixed(2)}` : '—')}</td>
                  <td>{a.signatureMode === "draw" ? "Drawn" : a.signatureMode === "checkbox" ? "Checkbox" : "Adopted"}</td>
                  <td><span className={styles.statusBadge + " " + styles.statusActive}>{a.agreementResponse?.toUpperCase() || "YES"}</span></td>
                  <td><div className={styles.actionBtns}>
                    <button className={styles.editBtn} onClick={() => setExpandedAgreement(expandedAgreement === a.id ? null : a.id)}>{expandedAgreement === a.id ? "Hide" : "Details"}</button>
                    <button className={styles.editBtn} onClick={async () => {
                      try { const token = localStorage.getItem('token'); const response = await fetch(`/api/admin/agreements/${a.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } }); if (!response.ok) throw new Error('Failed to load PDF'); const blob = await response.blob(); const url = URL.createObjectURL(blob); window.open(url, '_blank'); } catch (err) { toast.error('Failed to load PDF'); }
                    }}>View PDF</button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
            {expandedAgreement && (() => {
              const a = agreements.find(x => x.id === expandedAgreement);
              if (!a) return null;
              return (
                <div style={{ padding: "16px", background: "var(--color-tan-50)", borderRadius: "8px", marginTop: "8px", border: "0.5px solid var(--color-tan-300)" }}>
                  <h4 style={{ fontSize: "0.875rem", fontWeight: 700, marginBottom: "8px" }}>Checkbox States</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px", fontSize: "0.8rem", marginBottom: "12px" }}>
                    {a.checkboxes && Object.entries(a.checkboxes).map(([key, val]) => (<span key={key}>{val ? "[x]" : "[ ]"} {key}</span>))}
                  </div>
                  {a.signatureMode === "draw" && a.signatureData && (<div style={{ marginBottom: "12px" }}><h4 style={{ fontSize: "0.875rem", fontWeight: 700, marginBottom: "4px" }}>Signature</h4><img src={a.signatureData} alt="Signature" style={{ maxWidth: "200px", border: "0.5px solid var(--color-tan-300)", borderRadius: "4px" }} /></div>)}
                  {a.signatureMode === "adopt" && a.adoptedName && (<div style={{ marginBottom: "12px" }}><h4 style={{ fontSize: "0.875rem", fontWeight: 700, marginBottom: "4px" }}>Adopted Signature</h4><span style={{ fontStyle: "italic", fontSize: "1.1rem" }}>{a.adoptedName}</span></div>)}
                  {a.agreementText && (
                    <details style={{ marginBottom: "12px" }}>
                      <summary style={{ fontSize: "0.875rem", fontWeight: 700, cursor: "pointer" }}>Agreement Text</summary>
                      <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.75rem", maxHeight: "300px", overflow: "auto", background: "#fff", border: "0.5px solid var(--color-tan-300)", borderRadius: "6px", padding: "8px", marginTop: "6px" }}>{a.agreementText}</pre>
                    </details>
                  )}
                  <div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
                    <div>Signed: {new Date(a.consentTimestamp || a.createdAt).toLocaleString()}</div>
                    {a.termsVersion && <div>Terms Version: {a.termsVersion}</div>}
                    {a.membershipTier && <div>Membership Tier: {a.membershipTier}</div>}
                    {a.membershipMonthlyRate != null && <div>Monthly Rate: {formatCents(a.membershipMonthlyRate)}/mo</div>}
                    {a.supplyLabel && <div>Supply: {a.supplyLabel}{a.internalMonths ? ` (${a.internalMonths} month${a.internalMonths === 1 ? "" : "s"} internal)` : ""}</div>}
                    <div>IP: {a.ipAddress || "N/A"}</div><div>User Agent: {a.userAgent || "N/A"}</div>{a.orderId && <div>Order ID: {a.orderId}</div>}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Support Tickets Section — Moved between Agreements and Notes */}
      <div className={styles.supportTicketsSection}>
        <div className={styles.ticketsSectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2 className={styles.ticketsSectionTitle}>Support Tickets</h2>
            {supportTickets.length > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.25rem 0.625rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                background: openTicketCount > 0 ? '#eff6ff' : '#f3f4f6',
                color: openTicketCount > 0 ? '#2563eb' : '#6b7280',
                border: `1px solid ${openTicketCount > 0 ? '#bfdbfe' : '#e5e7eb'}`
              }}>
                Open {openTicketCount} | Closed {closedTicketCount}
              </span>
            )}
          </div>
          {hasPermission('support_tickets.edit') && (
            <button className={styles.openTicketBtn} onClick={() => setOpenTicketModalOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
              Open Ticket
            </button>
          )}
        </div>
        {ticketsLoading ? (
          <div className={styles.loadingState}><LoadingSpinner size="small" /></div>
        ) : ticketsError && supportTickets.length === 0 ? (
          <ListLoadError message={ticketsError} onRetry={() => loadSupportTickets()} />
        ) : supportTickets.length === 0 ? (
          <div className={styles.emptyStateBox}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>No support tickets have been opened for this member yet.</p>
          </div>
        ) : (
          <div className={styles.ticketsList}>
            {supportTickets.map(ticket => {
              const statusConf = ticketStatusConfig[ticket.status] || ticketStatusConfig.open;
              const priorityConf = ticket.priority ? ticketPriorityConfig[ticket.priority] : null;
              return (
                <div key={ticket.id} className={`${styles.ticketCard} ${expandedTicket === ticket.id ? styles.ticketExpanded : ''}`}>
                  <div className={styles.ticketHeader} onClick={() => loadTicketMessages(ticket.id)}>
                    <div className={styles.ticketInfo}>
                      <span className={styles.ticketNumber}>#{ticket.ticketNumber || ticket.id.slice(0, 8)}</span>
                      <span className={styles.ticketSubject}>{ticket.subject}</span>
                      {ticket.metadata?.createdByAdminName && (
                        <span style={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic' }}>Created by {ticket.metadata.createdByAdminName}</span>
                      )}
                    </div>
                    <div className={styles.ticketMeta}>
                      <span style={{
                        display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600,
                        background: statusConf.bgColor, color: statusConf.color
                      }}>{statusConf.label}</span>
                      {priorityConf && (
                        <span style={{
                          display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600,
                          background: priorityConf.bgColor, color: priorityConf.color
                        }}>{priorityConf.label}</span>
                      )}
                      {ticket.source && (
                        <span style={{
                          display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600,
                          background: '#f0fdf4', color: '#16a34a'
                        }}>{sourceLabels[ticket.source] || ticket.source}</span>
                      )}
                      <span className={styles.ticketDate}>
                        <span className={styles.ticketCreatedDate}>{formatAbsoluteTime(ticket.createdAt)}</span>
                        {ticket.updatedAt && ticket.updatedAt !== ticket.createdAt && (
                          <span className={styles.ticketUpdatedDate}>Updated: {formatAbsoluteTime(ticket.updatedAt)}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1rem', borderTop: '0.5px solid var(--color-tan-300, #f3e5d0)' }}>
                    {ticket.assignedTo ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-charcoal, #2d2d2d)', marginRight: 'auto' }}>
                        <InitialsAvatar firstName={ticket.assignedTo.firstName} lastName={ticket.assignedTo.lastName} size="sm" />
                        {ticket.assignedTo.firstName || ticket.assignedTo.fullName}
                      </span>
                    ) : hasPermission('support_tickets.edit') ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClaimProfileTicket(ticket); }}
                        disabled={claimingTicket === ticket.id}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: '8px', border: 'none', background: 'var(--color-primary, #f97316)', color: '#fff', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', marginRight: 'auto' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="22" y1="11" x2="16" y2="11"></line></svg>
                        {claimingTicket === ticket.id ? 'Claiming...' : 'Claim'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleViewTicketDetail(ticket.id); }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: '8px', border: '0.5px solid var(--color-tan-300, #f3e5d0)', background: '#fff', color: 'var(--color-charcoal, #2d2d2d)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                      View
                    </button>
                  </div>
                  {expandedTicket === ticket.id && (
                    <div className={styles.ticketBody}>
                      {ticketMessagesError[ticket.id] ? (
                        <ListLoadError
                          message={ticketMessagesError[ticket.id]}
                          onRetry={(e) => { e?.stopPropagation(); fetchTicketMessages(ticket.id); }}
                        />
                      ) : ticketMessages[ticket.id] ? (
                        <div className={styles.ticketMessages}>
                          {ticketMessages[ticket.id].map((msg, idx) => (
                            <div key={idx} className={`${styles.ticketMessage} ${msg.senderType === 'admin' ? styles.adminMessage : ''}`}>
                              <div className={styles.messageMeta}>
                                <span className={styles.messageSender}>{msg.sender ? `${msg.sender.firstName || ''} ${msg.sender.lastName || ''}`.trim() || msg.sender.email : 'System'}</span>
                                <span className={styles.messageTime}>{formatAbsoluteTime(msg.createdAt)}</span>
                              </div>
                              <p className={styles.messageContent}>{msg.content}</p>
                            </div>
                          ))}
                        </div>
                      ) : (<div className={styles.loadingState}><LoadingSpinner size="small" /></div>)}
                      {ticket.status !== 'closed' && (
                        <button className={styles.closeTicketBtn} onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id); }} disabled={closingTicket === ticket.id}>
                          {closingTicket === ticket.id ? 'Closing...' : 'Close Ticket'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div className={styles.notesSection}>
        <div className={styles.notesSectionHeader}>
          <h2 className={styles.notesSectionTitle}>Notes</h2>
          <button className={styles.sortToggle} onClick={() => { const newOrder = notesSortOrder === 'desc' ? 'asc' : 'desc'; setNotesSortOrder(newOrder); loadNotes(1); }}
            title={notesSortOrder === 'desc' ? 'Showing newest first' : 'Showing oldest first'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {notesSortOrder === 'desc' ? (<path d="M3 4h13M3 8h9M3 12h5M15 12l4 4m0 0l4-4m-4 4V4" />) : (<path d="M3 4h13M3 8h9M3 12h5M19 12l-4-4m0 0l-4 4m4-4v12" />)}
            </svg>
            {notesSortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          </button>
        </div>

        {/* Filter Tabs */}
        <div className={styles.noteFilterTabs}>
          {[{ key: 'all', label: 'All' }, { key: 'notes', label: 'Notes' }, { key: 'actions', label: 'Actions' }, { key: 'interactions', label: 'Interactions' }].map(tab => (
            <button key={tab.key} className={`${styles.noteFilterTab} ${noteFilter === tab.key ? styles.activeFilterTab : ''}`} onClick={() => setNoteFilter(tab.key)}>{tab.label}</button>
          ))}
        </div>

        {/* Add Note Form */}
        <div className={styles.addNoteForm}>
          <textarea className={`${styles.noteTextarea} ${newNoteExpanded ? styles.expanded : ''}`} placeholder="Add a note about this client..."
            value={newNoteContent} onChange={(e) => setNewNoteContent(e.target.value.slice(0, 2000))} onFocus={() => setNewNoteExpanded(true)} rows={newNoteExpanded ? 6 : 3} disabled={savingNote} />
          <div className={styles.noteFormFooter}>
            <span className={`${styles.charCounter} ${newNoteContent.length >= 1900 ? styles.nearLimit : ''}`}>{newNoteContent.length}/2000</span>
            {(newNoteExpanded || newNoteContent.length > 0) && (
              <div className={styles.noteFormActions}>
                <button className={styles.cancelNoteBtn} onClick={handleCancelNote} disabled={savingNote}>Cancel</button>
                <Button size="small" onClick={handleCreateNote} disabled={!newNoteContent.trim() || savingNote}>{savingNote ? 'Saving...' : 'Add Note'}</Button>
              </div>
            )}
          </div>
        </div>

        {notesLoading ? (<div className={styles.notesLoading}><LoadingSpinner size="small" /></div>
        ) : notesError && notes.length === 0 ? (
          <ListLoadError message={notesError} onRetry={() => loadNotes(1)} />
        ) : filteredNotes.length === 0 ? (
          <div className={styles.emptyStateBox}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>{noteFilter === 'all' ? 'No notes have been added yet.' : `No ${noteFilter} found`}</p>
            {noteFilter !== 'all' && <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Try a different filter</span>}
          </div>
        ) : (
          <div className={styles.notesList}>
            {(() => {
              const groupedNotes = groupNotesByDate(filteredNotes);
              const groupOrder = ['Today', 'Yesterday', 'This Week', 'Older'];
              return groupOrder.map(groupName => {
                const groupNotes = groupedNotes[groupName];
                if (!groupNotes || groupNotes.length === 0) return null;
                return (
                  <div key={groupName} className={styles.dateGroup}>
                    <div className={styles.dateGroupHeader}>{groupName}</div>
                    {groupNotes.map((note) => {
                      const typeConfig = noteTypeConfig[note.noteType] || noteTypeConfig.manual;
                      return (
                        <div key={note.id} className={`${styles.noteCard} ${note._optimistic ? styles.optimistic : ''}`} style={{ borderLeftColor: typeConfig.borderColor }}>
                          <div className={styles.noteHeader}>
                            <div className={styles.noteMetaLeft}>
                              {(() => {
                                const authorFirst = note.authorName?.split(' ')[0] || note.author?.firstName || '';
                                const authorLast = note.authorName?.split(' ').slice(1).join(' ') || note.author?.lastName || '';
                                const isSystem = !note.authorName && !note.author?.firstName;
                                return isSystem ? (
                                  <div className={styles.systemIcon} title="System">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                                  </div>
                                ) : (<InitialsAvatar firstName={authorFirst} lastName={authorLast} color={typeConfig.color} size="sm" />);
                              })()}
                              {note.noteType !== 'manual' && (<span className={styles.noteTypeBadge} style={{ backgroundColor: typeConfig.bgColor, color: typeConfig.color, borderColor: typeConfig.borderColor }}>{typeConfig.label}</span>)}
                              <span className={styles.noteAuthor}>{note.authorName || (note.author?.firstName ? `${note.author?.firstName || ''} ${note.author?.lastName || ''}`.trim() : 'System')}</span>
                            </div>
                            <span className={styles.noteTime}>
                              <span className={styles.noteAbsoluteTime}>{formatAbsoluteTime(note.createdAt)}</span>
                              <span className={styles.noteRelativeTime}>{formatRelativeTime(note.createdAt)}</span>
                            </span>
                          </div>
                          <div className={styles.noteContent}>{linkifyText(note.content)}</div>
                          {note.isPinned && (<div className={styles.pinnedIndicator}><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M16 4a1 1 0 0 0-1.5-.866l-7 4a1 1 0 0 0-.5.866v8a1 1 0 0 0 .5.866l7 4A1 1 0 0 0 16 20V4z" /></svg>Pinned</div>)}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {notesPagination && notesPagination.totalPages > 1 && (
          <div className={styles.notesPagination}>
            <button className={styles.paginationBtn} disabled={notesPagination.page <= 1} onClick={() => loadNotes(notesPagination.page - 1)}>Previous</button>
            <span className={styles.paginationInfo}>Page {notesPagination.page} of {notesPagination.totalPages}</span>
            <button className={styles.paginationBtn} disabled={notesPagination.page >= notesPagination.totalPages} onClick={() => loadNotes(notesPagination.page + 1)}>Next</button>
          </div>
        )}
      </div>

      {/* Activity Timeline Section */}
      <div className={styles.notesSection} style={{ marginTop: '1.5rem' }}>
        <div className={styles.notesSectionHeader}>
          <h2 className={styles.notesSectionTitle}>Activity Timeline</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <select style={{ padding: '0.375rem 0.625rem', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.75rem', fontFamily: 'var(--font-sans, "Inter Tight"), sans-serif', color: '#374151', background: '#fff', cursor: 'pointer' }}
              value={activityFilter} onChange={(e) => { setActivityFilter(e.target.value); loadActivityLog(1, e.target.value); }}>
              <option value="all">All Activity</option>
              <option value="note_added,note_edited,note_deleted,note_pinned">Notes</option>
              <option value="ticket_created,ticket_updated,ticket_closed,ticket_reopened,ticket_reply">Tickets</option>
              <option value="profile_updated,password_reset,account_deactivated,account_reactivated">Account</option>
              <option value="interaction_logged,welcome_call_logged,called_status_changed">Interactions</option>
              <option value="email_sent">Emails</option>
            </select>
            <button className={styles.sortToggle} onClick={() => loadActivityLog(1)} title="Refresh activity log">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6M23 20v-6h-6" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" /></svg>
            </button>
          </div>
        </div>

        {activityLoading ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}><LoadingSpinner /></div>
        ) : activityError && activityLog.length === 0 ? (
          <ListLoadError message={activityError} onRetry={() => loadActivityLog(1)} />
        ) : activityLog.length === 0 ? (
          <div className={styles.emptyStateBox}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <p style={{ margin: '0.5rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>No interaction history yet.</p>
          </div>
        ) : (
          <div className={styles.activityTimeline}>
            {activityLog.map((entry) => {
              const actionIcons = {
                note_added: { icon: '📝', color: '#6b7280' }, note_edited: { icon: '✏️', color: '#6b7280' },
                note_deleted: { icon: '🗑️', color: '#ef4444' }, note_pinned: { icon: '📌', color: '#f59e0b' },
                interaction_logged: { icon: '📞', color: '#7c3aed' }, welcome_call_logged: { icon: '👋', color: '#10b981' },
                password_reset: { icon: '🔑', color: '#ea580c' }, account_deactivated: { icon: '🚫', color: '#dc2626' },
                account_reactivated: { icon: '✅', color: '#16a34a' }, profile_updated: { icon: '👤', color: '#2563eb' },
                email_sent: { icon: '📧', color: '#8b5cf6' }, ticket_created: { icon: '🎫', color: '#0891b2' },
                ticket_updated: { icon: '🎫', color: '#0891b2' }, ticket_closed: { icon: '✔️', color: '#6b7280' },
                ticket_reopened: { icon: '🔓', color: '#d97706' }, ticket_reply: { icon: '💬', color: '#0891b2' },
                called_status_changed: { icon: '📱', color: '#7c3aed' }, agreement_updated: { icon: '📋', color: '#2563eb' },
                order_status_changed: { icon: '📦', color: '#ea580c' }
              };
              const iconConfig = actionIcons[entry.actionType] || { icon: '⚡', color: '#6b7280' };

              // System vs Admin attribution
              const isSystemAction = !entry.adminId && !entry.adminName;
              const triggeredByAdmin = entry.metadata?.adminTrigger || entry.metadata?.triggeredByAdmin;

              return (
                <div key={entry.id} className={styles.activityEntry}>
                  <div className={styles.activityIcon} style={{ backgroundColor: iconConfig.color + '15', color: iconConfig.color }}>
                    <span style={{ fontSize: '0.875rem' }}>{iconConfig.icon}</span>
                  </div>
                  <div className={styles.activityContent}>
                    <div className={styles.activityHeader}>
                      <span className={styles.activityLabel}>{entry.actionLabel}</span>
                      {isSystemAction ? (
                        <span className={styles.activityAdmin} style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                          {triggeredByAdmin ? `System, triggered by ${triggeredByAdmin}` : 'System'}
                        </span>
                      ) : entry.adminName ? (
                        <span className={styles.activityAdmin}>by {entry.adminName}</span>
                      ) : null}
                    </div>
                    {entry.details && (
                      <div className={styles.activityDetails}>{entry.details.length > 120 ? entry.details.substring(0, 120) + '...' : entry.details}</div>
                    )}
                    <div className={styles.activityTime}>
                      {formatAbsoluteTime(entry.createdAt)}<span style={{ margin: '0 0.25rem' }}>·</span>{formatRelativeTime(entry.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })}

            {activityPagination && activityPagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                {activityPagination.page > 1 && (<button className={styles.sortToggle} onClick={() => loadActivityLog(activityPagination.page - 1)}>Previous</button>)}
                <span style={{ fontSize: '0.75rem', color: '#6b7280', alignSelf: 'center' }}>Page {activityPagination.page} of {activityPagination.totalPages}</span>
                {activityPagination.page < activityPagination.totalPages && (<button className={styles.sortToggle} onClick={() => loadActivityLog(activityPagination.page + 1)}>Next</button>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== MODALS ===== */}

      {/* Reset Password Modal */}
      <Modal isOpen={resetPasswordModalOpen} onClose={() => setResetPasswordModalOpen(false)} title="Reset Password" size="small">
        <div className={styles.modalContent}>
          <p>Are you sure you want to send a password reset email to <strong>{client.email}</strong>?</p>
          <p className={styles.modalNote}>The user will receive an email with a link to reset their password. The link will expire in 1 hour.</p>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(false)} disabled={sendingReset}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={sendingReset}>{sendingReset ? 'Sending...' : 'Send Reset Email'}</Button>
          </div>
        </div>
      </Modal>

      {/* Resend Welcome Email Modal */}
      <Modal isOpen={welcomeEmailModalOpen} onClose={() => setWelcomeEmailModalOpen(false)} title="Resend Welcome Email" size="small">
        <div className={styles.modalContent}>
          <p>Are you sure you want to send a welcome email to <strong>{client.email}</strong>?</p>
          <p className={styles.modalNote}>The user will receive a welcome email with getting started instructions.{!client.isVerified && ' A verification link will also be included.'}</p>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setWelcomeEmailModalOpen(false)} disabled={sendingWelcome}>Cancel</Button>
            <Button onClick={handleResendWelcome} disabled={sendingWelcome}>{sendingWelcome ? 'Sending...' : 'Send Welcome Email'}</Button>
          </div>
        </div>
      </Modal>

      {/* Force Logout Modal */}
      <Modal isOpen={forceLogoutModalOpen} onClose={() => setForceLogoutModalOpen(false)} title="Force Logout" size="small">
        <div className={styles.modalContent}>
          <p>Are you sure you want to terminate all sessions for <strong>{client.email}</strong>?</p>
          <p className={styles.modalWarning}>This will immediately log the user out of all devices. They will need to sign in again to access their account.</p>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setForceLogoutModalOpen(false)} disabled={forcingLogout}>Cancel</Button>
            <Button variant="danger" onClick={handleForceLogout} disabled={forcingLogout}>{forcingLogout ? 'Terminating...' : 'Terminate Sessions'}</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Contact Modal */}
      <Modal isOpen={editContactModalOpen} onClose={() => setEditContactModalOpen(false)} title="Edit Contact Info" size="small">
        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Email</label>
            <input type="email" className={`${styles.formInput} ${contactErrors.email ? styles.inputError : ''}`} value={editContactData.email} onChange={(e) => setEditContactData({ ...editContactData, email: e.target.value })} placeholder="Enter email address" />
            {contactErrors.email && (<span className={styles.errorText}>{contactErrors.email}</span>)}
            {editContactData.email !== client.email && (<p className={styles.formHint}>Changing email will reset verification status</p>)}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Phone</label>
            <input type="tel" className={`${styles.formInput} ${contactErrors.phone ? styles.inputError : ''}`} value={editContactData.phone} onChange={(e) => setEditContactData({ ...editContactData, phone: e.target.value })} placeholder="Enter 10-digit phone number" />
            {contactErrors.phone && (<span className={styles.errorText}>{contactErrors.phone}</span>)}
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Street Address</label>
            <input type="text" className={styles.formInput} value={editContactData.street} onChange={(e) => setEditContactData({ ...editContactData, street: e.target.value })} placeholder="Enter street address" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>City</label>
              <input type="text" className={styles.formInput} value={editContactData.city} onChange={(e) => setEditContactData({ ...editContactData, city: e.target.value })} placeholder="City" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>State</label>
              <input type="text" className={styles.formInput} value={editContactData.state} onChange={(e) => setEditContactData({ ...editContactData, state: e.target.value })} placeholder="State" maxLength={2} style={{ textTransform: 'uppercase' }} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>ZIP Code</label>
            <input type="text" className={styles.formInput} value={editContactData.zipCode} onChange={(e) => setEditContactData({ ...editContactData, zipCode: e.target.value })} placeholder="ZIP Code" maxLength={10} />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Agency</label>
            <select className={styles.formSelect} value={editContactData.agencyId} onChange={(e) => setEditContactData({ ...editContactData, agencyId: e.target.value })}>
              <option value="">None</option>
              {agencies.map((agency) => (<option key={agency.id} value={agency.id}>{agency.name}</option>))}
            </select>
          </div>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setEditContactModalOpen(false)} disabled={savingContact}>Cancel</Button>
            <Button onClick={handleSaveContact} disabled={savingContact}>{savingContact ? 'Saving...' : 'Save Changes'}</Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate/Reactivate Modal */}
      <Modal isOpen={deactivateModalOpen} onClose={() => setDeactivateModalOpen(false)} title={client.isActive ? 'Deactivate Account' : 'Reactivate Account'} size="small">
        <div className={styles.modalContent}>
          {client.isActive ? (
            <><p>Are you sure you want to deactivate the account for <strong>{client.email}</strong>?</p><p className={styles.modalWarning}>This will prevent the user from logging in. All active sessions will be terminated immediately. The account data will be preserved and can be reactivated later.</p></>
          ) : (
            <><p>Are you sure you want to reactivate the account for <strong>{client.email}</strong>?</p><p className={styles.modalNote}>This will allow the user to log in again with their existing credentials.</p></>
          )}
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setDeactivateModalOpen(false)} disabled={togglingActive}>Cancel</Button>
            <Button variant={client.isActive ? 'danger' : 'primary'} onClick={handleToggleActive} disabled={togglingActive}>{togglingActive ? (client.isActive ? 'Deactivating...' : 'Reactivating...') : (client.isActive ? 'Deactivate Account' : 'Reactivate Account')}</Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Dependent Modal */}
      <Modal isOpen={dependentModalOpen} onClose={() => setDependentModalOpen(false)} title={editingDependent ? 'Edit Dependent' : 'Add Dependent'} size="medium">
        <div className={styles.modalContent}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>First Name *</label><input type="text" className={styles.formInput} value={dependentFormData.firstName} onChange={(e) => setDependentFormData({ ...dependentFormData, firstName: e.target.value })} placeholder="Enter first name" /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>Last Name *</label><input type="text" className={styles.formInput} value={dependentFormData.lastName} onChange={(e) => setDependentFormData({ ...dependentFormData, lastName: e.target.value })} placeholder="Enter last name" /></div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>Relationship *</label><select className={styles.formSelect} value={dependentFormData.relationship} onChange={(e) => setDependentFormData({ ...dependentFormData, relationship: e.target.value })}><option value="spouse">Spouse</option><option value="child">Child</option><option value="other">Other</option></select></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>Date of Birth</label><input type="date" className={styles.formInput} value={dependentFormData.dateOfBirth} onChange={(e) => setDependentFormData({ ...dependentFormData, dateOfBirth: e.target.value })} max={new Date().toISOString().split('T')[0]} /></div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>Email {!editingDependent && "*"}</label><input type="email" className={styles.formInput} value={dependentFormData.email} onChange={(e) => setDependentFormData({ ...dependentFormData, email: e.target.value })} placeholder="Enter email address" /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>Phone</label><input type="tel" className={styles.formInput} value={dependentFormData.phone} onChange={(e) => setDependentFormData({ ...dependentFormData, phone: e.target.value })} placeholder="Enter phone number" /></div>
          </div>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => setDependentModalOpen(false)} disabled={savingDependent}>Cancel</Button>
            <Button onClick={handleSaveDependent} disabled={savingDependent}>{savingDependent ? 'Saving...' : (editingDependent ? 'Update' : 'Add Dependent')}</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Dependent Modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDeletingDependent(null); }} title="Remove Dependent" size="small">
        <div className={styles.modalContent}>
          <p>Are you sure you want to remove <strong>{deletingDependent?.firstName} {deletingDependent?.lastName}</strong> as a dependent?</p>
          <p className={styles.modalWarning}>This action cannot be undone.</p>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => { setDeleteModalOpen(false); setDeletingDependent(null); }} disabled={confirmingDelete}>Cancel</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={confirmingDelete}>{confirmingDelete ? 'Removing...' : 'Remove Dependent'}</Button>
          </div>
        </div>
      </Modal>

      {/* Create-ticket modal, shared with the Support Tickets page. A memberId is
          passed here, so the modal renders no Client field and targets this member. */}
      <OpenTicketModal
        isOpen={openTicketModalOpen}
        onClose={() => setOpenTicketModalOpen(false)}
        memberId={id}
        onCreated={() => { loadSupportTickets(); loadNotes(1); }}
      />

      {/* Shared ticket detail modal (same component as the Support Tickets page) */}
      <TicketDetailModal
        ticketId={viewTicketId}
        isOpen={viewTicketModalOpen}
        onClose={() => setViewTicketModalOpen(false)}
        onUpdated={loadSupportTickets}
      />

      {/* Log Interaction Modal */}
      <Modal isOpen={interactionModalOpen} onClose={() => { setInteractionModalOpen(false); setInteractionForm({ type: '', notes: '', followUpDate: '', relatedTicketId: '' }); }} title="Log Interaction" size="medium">
        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Interaction Type *</label>
            <select className={styles.formSelect} value={interactionForm.type} onChange={(e) => setInteractionForm(prev => ({ ...prev, type: e.target.value }))} required>
              <option value="">Select type...</option>
              {interactionTypes.map(t => (<option key={t.value} value={t.value}>{t.icon} {t.label}</option>))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Description / Notes *</label>
            <textarea className={`${styles.formInput} ${styles.ticketTextarea}`} value={interactionForm.notes} onChange={(e) => setInteractionForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Describe the interaction..." rows={4} required />
          </div>
          <div className={styles.ticketFormRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Follow-Up Date (optional)</label>
              <input type="date" className={styles.formInput} value={interactionForm.followUpDate} onChange={(e) => setInteractionForm(prev => ({ ...prev, followUpDate: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Related Ticket (optional)</label>
              <select className={styles.formSelect} value={interactionForm.relatedTicketId} onChange={(e) => setInteractionForm(prev => ({ ...prev, relatedTicketId: e.target.value }))}>
                <option value="">None</option>
                {supportTickets.filter(t => t.status !== 'closed' && t.status !== 'resolved').map(t => (
                  <option key={t.id} value={t.id}>#{t.ticketNumber || t.id.slice(0, 8)} - {t.subject}</option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.modalActions}>
            <Button variant="outline" onClick={() => { setInteractionModalOpen(false); setInteractionForm({ type: '', notes: '', followUpDate: '', relatedTicketId: '' }); }} disabled={savingInteraction} type="button">Cancel</Button>
            <Button onClick={handleLogInteraction} disabled={savingInteraction || !interactionForm.type || !interactionForm.notes.trim()}>{savingInteraction ? 'Saving...' : 'Log Interaction'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientDetailPage;
